import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { Vehicle } from "../../../models/vehicle";
import { ExportXLSPDF } from "../../../commons/export-xls-pdf.service";
import { Park } from "../../../models/park";
import { User } from "../../../models/user";
import { AuthService } from "../../../auth-n-routing/auth-service.service";
import { ParksHandlerService } from "../../../api-handlers/parks-handler.service";
import { VehiclesHandlerService } from "../../../api-handlers/vehicles-handler.service";
import { TravelsHandlerService } from "../../../api-handlers/travels-handler.service";
import { Travel } from "../../../models/travel";
import { DateHandlerService } from "../../../commons/date-handler.service";
import { RefillsHandlerService } from "../../../api-handlers/refills-handler.service";
import { Refill } from "../../../models/refill";
import { ChangeKmTravelComponent } from "../../../dialogs/change-km-travel/change-km-travel.component";
import { ChangeRefillsComponent } from "../../../dialogs/change-refills/change-refills.component";

@Component({
  selector: "app-backoffice",
  templateUrl: "./backoffice.component.html",
  styleUrls: ["./backoffice.component.css"],
})
export class BackofficeComponent implements OnInit {
  @ViewChildren("plateInput") plateInput;
  @ViewChild(ChangeKmTravelComponent, { static: false })
  changeKmTravelDialog: ChangeKmTravelComponent;
  @ViewChild(ChangeRefillsComponent, { static: false })
  changeRefillsDialog: ChangeRefillsComponent;
  @Output() statusChanged = new EventEmitter();
  searched_plate = "";
  selected_park: Park = null;
  selected_refill: Refill = null;
  selected_vehicle: Vehicle = null;
  selected_travel: Travel = null;
  travels_page = 1;
  to_hide_next_tr = true;
  to_hide_prev_tr = true;
  refills_page = 1;
  to_hide_next_rf = true;
  to_hide_prev_rf = true;
  parks = [];
  vehicles = {};
  travels = [];
  refills = [];
  vehicles_filtered = [];

  constructor(
    public exportService: ExportXLSPDF,
    private authService: AuthService,
    private ngZone: NgZone,
    private vehicleHandler: VehiclesHandlerService,
    private travelsHandler: TravelsHandlerService,
    private refillsHandler: RefillsHandlerService,
    private parksHandler: ParksHandlerService,
    public dateHandler: DateHandlerService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.authService.authChange.subscribe((u: User) => {
      if (u) {
        this.reloadParks();
      }
    });
  }

  openChanged(park: Park) {
    if (park["isOpen"]) {
      this.selected_park = park;
      this.selected_vehicle = null;
      this.selected_travel = null;
      this.travels = [];
      this.getVehiclesFromPark(park);
    } else {
      this.selected_park = null;
    }
  }

  reloadParks() {
    this.parksHandler.getByAdminID().then((res) => {
      this.parks = [];
      res.forEach((park, _index) => {
        park["isOpen"] = false;
        this.parks.push(park);
        if (_index === res.length - 1) {
          park["isOpen"] = true;
        }
      });

      if (this.parks.length > 0) {
        this.openChanged(this.parks[res.length - 1]);
      }

      this.changeDetector.detectChanges();
    });
  }

  getVehiclesFromPark(park: Park) {
    this.reset(true);
    this.vehicleHandler.getByParkID(park.id).then((res) => {
        this.vehicles[park.id] = res;

        this.onVehicleChanged("");
        this.changeDetector.detectChanges();
    });
  }

  vehicleSelected(vehicle: Vehicle) {
    this.selected_vehicle = vehicle;
    if (this.searched_plate !== this.selected_vehicle.plate) {
      this.searched_plate = this.selected_vehicle.plate;
      this.onVehicleChanged(this.searched_plate);
    } else {
      this.searched_plate = this.selected_vehicle.plate;
      this.selected_travel = null;
      this.travels = [];
      this.travels_page = 1;
      this.to_hide_prev_tr = true;
      this.to_hide_next_tr = false;

      this.reloadMaxKM();
      this.reloadTravels();
    }
  }

  reloadMaxKM() {
    this.travelsHandler
      .getMaxKmByVehicleID(this.selected_vehicle)
      .then((res) => {
        this.selected_vehicle["max_kilometers"] = res.toString();
      })
      .catch((er) => {});
  }

  reset(input_also: boolean) {
    this.selected_vehicle = null;
    this.travels = [];
    this.refills = [];
    this.travels_page = 1;
    this.refills_page = 1;
    this.to_hide_prev_tr = true;
    this.to_hide_next_tr = false;
    this.to_hide_prev_rf = true;
    this.to_hide_next_rf = false;
    this.vehicles_filtered = [];
    if (input_also) {
      this.plateInput.forEach((inpu) => {
        inpu.nativeElement.value = "";
      });
    }
  }

  onVehicleChanged(vehicle_plate: string) {
    this.reset(false);
    this.vehicles[this.selected_park.id].forEach((veh) => {
      if (
        this.vehicles_filtered.length < 5 &&
        veh.plate.toLowerCase().includes(vehicle_plate.toLowerCase())
      ) {
        this.vehicles_filtered.push(veh);
      }
    });

    if (this.vehicles_filtered.length === 1) {
      this.vehicleSelected(this.vehicles_filtered[0]);
    }
  }

  refillSelected(refill: Refill) {
    this.selected_refill = refill;
  }

  nextTravelPage() {
    this.travels_page += 1;
    this.reloadTravels();
  }

  prevTravelPage() {
    if (this.travels_page !== 1) {
      this.travels_page -= 1;
      this.reloadTravels();
    }
  }

  nextRefillPage() {
    this.refills_page += 1;
    this.reloadRefills();
  }

  prevRefillPage() {
    if (this.refills_page !== 1) {
      this.refills_page -= 1;
      this.reloadRefills();
    }
  }

  travelSelected(travel: Travel) {
    this.selected_travel = travel;
    this.refills = [];
    this.refills_page = 1;
    this.to_hide_prev_rf = true;
    this.to_hide_next_rf = false;

    this.reloadRefills();
  }

  reloadTravels() {
    this.travelsHandler
      .getTravelsByVehicleID(
        this.selected_vehicle.id.toString(),
        this.travels_page
      )
      .then((data) => {
        this.ngZone.run(() => {
          if (data.length < 5) {
            this.to_hide_next_tr = true;
            if (data.length === 0 && this.travels_page !== 1) {
              this.travels_page -= 1;
            } else {
              this.travels = data;
            }
          } else {
            this.to_hide_next_tr = false;
            this.travels = data;
          }

          this.to_hide_prev_tr = this.travels_page === 1;
          this.changeDetector.detectChanges();
        });
      });
  }

  determineMotivationOther(travel: Travel): string {
    return travel.motivation === 3 ? travel.motivation_other : travel.motivation_name;
  }

  reloadRefills() {
    this.refillsHandler
      .getRefillsByTravelID(
        this.selected_travel.id.toString(),
        this.refills_page
      )
      .then((data) => {
        this.ngZone.run(() => {
          if (data.length < 5) {
            this.to_hide_next_rf = true;
            if (data.length === 0 && this.refills_page !== 1) {
              this.refills_page -= 1;
            } else {
              this.refills = data;
            }
          } else {
            this.to_hide_next_rf = false;
            this.refills = data;
          }

          this.to_hide_prev_rf = this.refills_page === 1;
		  this.changeDetector.detectChanges();
        });
      });
  }

  editTravel(travel) {
    /* alert('funzione in vase di sviluppo. aprire bootstrap dialog per la modifica dei chilometri del viaggio'); */
    this.changeKmTravelDialog.showDialog(travel);
    this.changeKmTravelDialog.onClose = (myData) => {
      this.reloadMaxKM();
      this.changeKmTravelDialog.modalRef.hide();
      this.statusChanged.next(undefined);
    };
  }

  editRefill(refill) {
    /* alert('funzione in vase di sviluppo. aprire bootstrap dialog per la modifica dei chilometri del rifornimento'); */
    this.changeRefillsDialog.showDialog(refill);
    this.changeRefillsDialog.onClose = (myData) => {
      this.changeRefillsDialog.modalRef.hide();
      this.statusChanged.next(undefined);
    };
  }
}
